/**
 * CORE-929 — покупка программы: правила экрана отдельно от его вёрстки
 * (§6 ТЗ, Макет 10).
 *
 * Flow макета — «Купить программу → подтверждение → платёжный контур → paid →
 * успешная покупка → Перейти к программе». Здесь живут первый, второй и
 * четвёртый шаги: что показать, что спросить у бэка и как понять исход. Третий
 * шаг — единственный, которого в проекте ещё нет; см. `paymentContourFor`.
 *
 * Правила вынесены из компонента намеренно: «программу нельзя купить, потому
 * что мастер поставил продажи на паузу» и «программа уже куплена» — это разные
 * ответы человеку, и проверяться они должны без рендера.
 */

import { IS_PROD_BUILD } from '../../config/flags';
import type {
  ProgramAccess,
  ProgramPurchase,
  PublicProgramPage,
} from '../../types/programs';

// ─────────────────────────────────────────────────────────────────────────────
// Платёжный контур
// ─────────────────────────────────────────────────────────────────────────────

/** Куда уводить человека платить. */
export interface PaymentHandoff {
  /** Адрес страницы оплаты у провайдера. */
  url: string;
}

/**
 * Подключён ли платёжный контур вообще.
 *
 * Настоящего провайдера у проекта по-прежнему нет — он не выбран (CORE-658,
 * CORE-1075). На стендах его место занимает песочница (CORE-1115): страница с
 * двумя кнопками, которая сообщает бэкенду тот же исход, что сообщил бы
 * провайдер. Купить за деньги нельзя нигде; проверить покупку целиком — можно
 * там, где песочница есть.
 *
 * Различает стенд и прод сборка, а не флаг из админки. Флаг здесь означал бы,
 * что бесплатную выдачу платного доступа можно включить на проде нажатием.
 * `IS_PROD_BUILD` выведен из адреса API, на который собран бандл, — знания
 * «мы на проде» не подделать переменной, которую забыли выставить.
 *
 * Ответ «да» — это только разрешение начать: адрес страницы оплаты приходит от
 * бэкенда вместе с покупкой (`paymentContourFor`), и последнее слово за ним.
 * Прод-бэкенд не отдаст адрес, даже если сюда каким-то образом доедет сборка
 * стенда.
 *
 * Спрашивается ДО создания покупки, поэтому отдельно от `paymentContourFor`:
 * там уже есть что оплачивать, здесь ещё нет.
 */
export function hasPaymentContour(): boolean {
  return !IS_PROD_BUILD;
}

/**
 * Платёжный контур для этой покупки — или `null`, если его нет.
 *
 * Источник правды — бэкенд: он заполняет `paymentUrl` только у неоплаченной
 * покупки и только там, где платить действительно есть где. Своего мнения у
 * фронта здесь нет и быть не должно — экран, уводящий в никуда, хуже экрана,
 * который честно говорит «пока нельзя».
 *
 * Когда появится настоящий провайдер, эта функция не изменится: сменится то,
 * что стоит за `paymentUrl`.
 */
export function paymentContourFor(purchase: ProgramPurchase): PaymentHandoff | null {
  return purchase.paymentUrl ? { url: purchase.paymentUrl } : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Можно ли эту программу купить
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Почему покупка закрыта. Строковый код, а не булево: причин шесть, и человеку
 * они звучат по-разному.
 *
 * `owned` в этот перечень не входит — «уже куплено» это не отказ, а другой
 * экран, и ведёт он не назад, а в саму программу.
 */
export type PurchaseBlock =
  /** Архив: страница живёт ради ссылок из истории, продаж на ней нет. */
  | 'archived'
  /** Мастер временно приостановил продажи. Вернётся. */
  | 'salesPaused'
  /** Скрыта модерацией. Видят только мастер и купившие. */
  | 'moderationHidden'
  /** CORE-1922: цена указана не в рублях — до перехода на рубли. Мастер её обновит. */
  | 'notRubPrice'
  /** Продажи закрыты, а причина из известных нам не подошла ни одна. */
  | 'notForSale'
  /** Цена не заполнена — купить нечего. */
  | 'notPriced'
  /** Платёжного контура в проекте ещё нет. */
  | 'noPaymentContour'
  /** CORE-1536: этой сборке нельзя вести человека к внешней оплате. */
  | 'notInApp';

const BLOCK_MESSAGES: Record<PurchaseBlock, string> = {
  archived: 'Программа в архиве — сейчас её не продают.',
  salesPaused: 'Мастер временно приостановил продажи этой программы.',
  moderationHidden: 'Программа сейчас недоступна.',
  notRubPrice: 'Покупка временно недоступна: мастер обновляет цену программы.',
  notForSale: 'Сейчас эту программу не продают.',
  notPriced: 'У программы не указана цена.',
  noPaymentContour:
    'Оплата этой программы пока не подключена. Мы заканчиваем настройку и откроем её в ближайшее время.',
  notInApp: 'Покупка этой программы недоступна в приложении.',
};

export function purchaseBlockMessage(block: PurchaseBlock): string {
  return BLOCK_MESSAGES[block];
}

/**
 * Что мешает нажать «Купить» — или `null`, если ничего.
 *
 * Порядок проверок — это порядок правды: программа, снятая с продажи, остаётся
 * непокупаемой независимо от того, подключён ли платёжный контур, и говорить ей
 * «оплата пока не подключена» значит обещать то, чего не будет.
 *
 * Про отсутствие контура спрашиваем последним и только у той программы, которую
 * иначе можно было бы купить: это временное состояние проекта, а не свойство
 * программы.
 *
 * CORE-1115: и только у той, за которую вообще надо платить. Бесплатная
 * программа (цена 0 — законная цена, а не пропуск) оформляется бэкендом мимо
 * платежа: доступ, чат и уведомление выдаются сразу. Отказывать ей из-за
 * отсутствия платёжного контура значило бы держать закрытым то, что открывается
 * без денег.
 *
 * CORE-928 (QA-ретест 04.09.2026): `forSale` — вывод бэкенда, а не отдельная
 * причина. Он гаснет от ЛЮБОЙ из них, в том числе от паузы продаж, поэтому
 * читать его как архив нельзя: у поставленной на паузу программы приходит
 * `programStatus: published, salesPaused: true, forSale: false,
 * archivedAtUtc: null`, а страница отвечала «Программа в архиве» — утверждение,
 * которого сервер не делал и которое мастер не подтвердил бы. Архив теперь
 * опознаётся только по статусу; погасший `forSale` без известной причины
 * закрывает покупку, но ничего не выдумывает про то, почему.
 *
 * CORE-1536: `externalPurchase` — решение `services/paymentSurface` (CORE-1535):
 * Android вне РФ и iOS к внешней оплате не ведут (Google Play Payments policy).
 * Проверяется раньше контура: «оплату скоро подключим» там было бы неправдой.
 * Бесплатную программу не трогает — платить за неё не нужно.
 */
export function purchaseBlock(
  page: PublicProgramPage,
  hasPaymentContour: boolean,
  externalPurchase = true,
): PurchaseBlock | null {
  if (page.moderationHidden) return 'moderationHidden';
  if (page.programStatus === 'archived') return 'archived';
  if (page.salesPaused) return 'salesPaused';
  // CORE-1922: продаём только в рублях. Бэк гасит forSale и сам, но причину
  // называем точнее общего «не продают» — и не рисуем «1000 $» к оплате.
  if (page.currency && page.currency.toUpperCase() !== 'RUB') return 'notRubPrice';
  if (!page.forSale) return 'notForSale';
  if (page.price == null) return 'notPriced';
  if (page.price > 0 && !externalPurchase) return 'notInApp';
  if (page.price > 0 && !hasPaymentContour) return 'noPaymentContour';
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Состояние экрана
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Экран покупки целиком. Различитель строковый, а не набор булевых флагов:
 * под конфигом jest (`strictNullChecks` выключен) TypeScript не сужает
 * объединение по булеву литералу, а «ждём оплату» и «оплата не прошла» обязаны
 * быть взаимоисключающими не только на словах.
 */
export type PurchaseStage =
  | { stage: 'loading' }
  /** Программу не отдали: нет такой, черновик, витрина мастера закрыта. */
  | { stage: 'notFound' }
  /** Сеть или бэк ответили не тем; текст — уже человеческий. */
  | { stage: 'error'; message: string }
  /** Подтверждение покупки — сумма, срок, CTA. `block` гасит CTA и объясняет. */
  | { stage: 'confirm'; page: PublicProgramPage; block: PurchaseBlock | null }
  /**
   * У человека висит неоплаченная покупка, и её надо дочитать: страница
   * программы отдаёт только её номер и статус, а экрану ожидания нужна она
   * сама. Промежуточное состояние, а не загрузка: программа уже известна, и
   * показывать её заново не нужно.
   */
  | { stage: 'resume'; page: PublicProgramPage; purchaseId: number }
  /** Покупка заведена, ждём исход от платёжного контура. */
  | { stage: 'pending'; page: PublicProgramPage; purchase: ProgramPurchase }
  /** Платёж не прошёл. Причина — от провайдера, её может и не быть. */
  | { stage: 'failed'; page: PublicProgramPage; purchase: ProgramPurchase }
  /** Оплачено: успех и «Перейти к программе». */
  | { stage: 'paid'; page: PublicProgramPage; purchase: ProgramPurchase | null }
  /** Доступ уже есть — покупать нечего, вести надо в программу. */
  | { stage: 'owned'; page: PublicProgramPage; access: ProgramAccess };

/**
 * С чего начинается экран у человека, который на него пришёл.
 *
 * Отдельная функция, потому что пришёл он не обязательно с витрины: по ссылке
 * из письма, из соседней вкладки, вернувшись со страницы оплаты. Человек,
 * который уже заплатил, обязан увидеть успех, а не предложение купить второй
 * раз, и решает это состояние покупки, а не то, откуда он пришёл.
 *
 * `hasAccess` проверяется раньше статуса покупки: доступ мог быть выдан не
 * покупкой вовсе (подарен, возвращён администратором), и тогда покупки под ним
 * нет, а доступ есть.
 */
export function initialStage(
  page: PublicProgramPage,
  hasPaymentContour: boolean,
  externalPurchase = true,
): PurchaseStage {
  const access = page.access;

  if (access?.hasAccess) return { stage: 'owned', page, access };

  if (access?.purchaseStatus === 'paid' && !access.accessExpired && !access.accessRevoked) {
    // Оплачено, но доступа почему-то нет: показываем успех и ведём в
    // программу — разбираться, почему он не доехал, дело не покупателя.
    return { stage: 'paid', page, purchase: null };
  }

  if (access?.purchaseStatus === 'pending' && access.purchaseId != null) {
    // Ушёл платить и вернулся. Вернуть его на кнопку «Купить» значит предложить
    // заплатить второй раз за то, что он, возможно, уже оплатил.
    return { stage: 'resume', page, purchaseId: access.purchaseId };
  }

  // Неудавшаяся покупка сюда не попадает намеренно: она закончилась, и
  // естественное состояние после неё — снова «купить», а не разбор прошлого
  // отказа. Экран «Оплата не прошла» показывается там, где отказ и случился.
  return { stage: 'confirm', page, block: purchaseBlock(page, hasPaymentContour, externalPurchase) };
}

/**
 * Куда встать, узнав исход покупки. Неизвестный статус считаем ожиданием: пока
 * бэк не сказал «paid» или «failed», доступа нет, и обещать его нельзя.
 */
export function stageFromPurchase(
  page: PublicProgramPage,
  purchase: ProgramPurchase,
): PurchaseStage {
  if (purchase.status === 'paid') return { stage: 'paid', page, purchase };
  if (purchase.status === 'failed') return { stage: 'failed', page, purchase };
  return { stage: 'pending', page, purchase };
}

/** Стоит ли ещё спрашивать бэк об исходе. Только у ожидания есть продолжение. */
export function shouldPollPurchase(stage: PurchaseStage): boolean {
  return stage.stage === 'pending';
}

// ─────────────────────────────────────────────────────────────────────────────
// Отказы бэка
// ─────────────────────────────────────────────────────────────────────────────

/**
 * «Доступ к программе уже есть» — единственный отказ 409, у которого в теле
 * лежит сама покупка. По ней он и опознаётся: остальные 409 отличаются только
 * свободным текстом сообщения, а ветвиться по нему значит сломаться от правки
 * копирайта.
 */
export function alreadyOwnedPurchase(error: unknown): ProgramPurchase | null {
  const body = error as { status?: number; purchase?: ProgramPurchase } | null;
  if (!body || body.status !== 409) return null;
  return body.purchase ?? null;
}

/**
 * Что показать человеку, когда покупка не завелась.
 *
 * Сообщения отказов бэк пишет по-русски и по делу («Мастер временно
 * приостановил продажи этой программы»), и они точнее любого нашего пересказа:
 * между «Купить» и нажатием мастер мог передумать. Поэтому берём его текст, а
 * своим отвечаем только там, где его нет.
 */
/**
 * CORE-1214 — отказ, который лечится входом, а не повтором.
 *
 * `POST /api/programs/{id}/purchase` отвечает гостю 401 с ПУСТЫМ телом (ноль
 * байт, без типа), поэтому общий разбор ответа честно сообщает «Не удалось
 * обработать ответ сервера» — фраза, которая ничего не объясняет и никуда не
 * ведёт. Человек при этом не сломал ничего: он просто не представился.
 *
 * Отдельная функция, а не проверка кода по месту, — чтобы граница была одна и
 * названа. Всё остальное входом не лечится и притворяться этим не должно:
 * 404 — это «программы нет», 409 — «уже куплено» или «продажи закрыты», 5xx —
 * поломка на сервере. Предложить вход в ответ на любой из них значит соврать и
 * увести человека делать бесполезное.
 */
export function isSignInRequired(error: unknown): boolean {
  return (error as { status?: number } | null | undefined)?.status === 401;
}

export function purchaseErrorMessage(error: unknown): string {
  const body = error as { message?: string; status?: number } | null;
  const message = body?.message?.trim();
  if (message) return message;
  if (body?.status === 404) return 'Программа не найдена.';
  return 'Не удалось начать покупку. Попробуйте ещё раз.';
}
